// Copyright (c) 2025 WiseTime. All rights reserved.

import { uniq } from "ramda"
import * as TimelineActions from "../../actions"
import { TimesheetFieldOptionsCache } from "../../slices/timeline"
import {
  BatchGetTimesheetFieldOptionsRequest,
  ListTimesheetFieldsRequest,
  TimesheetField,
} from "../../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { SelectedTimesheetFieldOption } from "../../../../generated/wisetime/user/type/selected_timesheet_field_option_pb"

type OptionsBySiteId = Record<string, SelectedTimesheetFieldOption.AsObject[]>

type TimesheetFieldCacheRefreshAction = 
  | ReturnType<typeof TimelineActions.listTimesheetFields>
  | ReturnType<typeof TimelineActions.batchGetTimesheetFieldOptions>

export const generateTimesheetFieldCachesRefreshActions = (
  optionsBySiteId: OptionsBySiteId,
  cachedTimesheetFields: TimesheetField.AsObject[],
  cachedTimesheetFieldOptions: TimesheetFieldOptionsCache = {},
): TimesheetFieldCacheRefreshAction[] => {
  const actions: TimesheetFieldCacheRefreshAction[] = []

  Object.keys(optionsBySiteId).forEach(siteId => {
    const timesheetFieldsAreCached = cachedTimesheetFields.find(field => (
      field.siteId === siteId
    )) !== undefined
    
    if (!timesheetFieldsAreCached) {
      actions.push(TimelineActions.listTimesheetFields({ request: new ListTimesheetFieldsRequest().setSiteId(siteId) }))
    }
    
    const options = optionsBySiteId[siteId]
    if (!options?.length) {
      return
    }
    
    const uniqueOptionIds = uniq(options.map(option => option.timesheetFieldOptionId))
    const uncachedOptionIds = uniqueOptionIds.filter(optionId => !cachedTimesheetFieldOptions[optionId])
    
    if (uncachedOptionIds.length > 0) {
      actions.push(
        TimelineActions.batchGetTimesheetFieldOptions(
          new BatchGetTimesheetFieldOptionsRequest()
            .setSiteId(siteId)
            .setIdsList(uncachedOptionIds)
        ),
      )
    }
  })

  return actions
}
